import { useEffect, useMemo, useState, useRef } from "react";

import classnames from "classnames";
import { isPresent, isNotEmpty } from "neetocist";
import useLocalStorage from "neetocommons/react-utils/useLocalStorage";
import { Button, Typography } from "neetoui";
import PropTypes from "prop-types";
import { findLast, is, not } from "ramda";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";

import Chevron from "./Components/Chevron";
import Header from "./Components/Header";
import Links from "./Components/Links";
import SelectedLink from "./Components/Links/SelectedLink";
import {
  SELECTED_NAV_LINK_ROUTE_STORAGE_KEY,
  SIDEBAR_WIDTH_KEY,
  MIN_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
} from "./constants";
import "./sidebar.scss";
import {
  getActiveConfigurePageLink,
  getSidebarStateLocalStorageKey,
  enableDraggingStyles,
  disableDraggingStyles,
} from "./utils";

import useKeyboardShortcutsPaneState from "../KeyboardShortcuts/hooks/useKeyboardShortcutsPaneState";

const Sidebar = ({
  navLinks,
  moreNavLinks = [],
  customLogo,
  isCountsLoading = false,
  showWorkspaceName = false,
  isExpandable = true,
}) => {
  const [isKeyboardShortcutsPaneOpen] = useKeyboardShortcutsPaneState();

  const [isSidebarPaneOpen, setIsSidebarPaneOpen] = useState(false);
  const [selectedNavLinkRoute, setSelectedNavLinkRoute] = useLocalStorage(
    SELECTED_NAV_LINK_ROUTE_STORAGE_KEY,
    null
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useLocalStorage(
    getSidebarStateLocalStorageKey(),
    false
  );

  const [sidebarWidth, setSidebarWidth] = useLocalStorage(
    SIDEBAR_WIDTH_KEY,
    `${DEFAULT_SIDEBAR_WIDTH}px`
  );

  const sidebarRef = useRef(null);
  const isMouseDownRef = useRef(false);
  const startXRef = useRef(null);
  const startWidthRef = useRef(null);

  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const isLargerScreen = () => window.innerWidth > 768;
  const isSidebarClosed = isLargerScreen()
    ? isSidebarCollapsed
    : not(isSidebarPaneOpen);

  useEffect(() => {
    if (isKeyboardShortcutsPaneOpen) {
      setIsSidebarPaneOpen(false);
      setIsSidebarCollapsed(true);
    } else if (!isKeyboardShortcutsPaneOpen && isLargerScreen()) {
      setIsSidebarPaneOpen(true);
      setIsSidebarCollapsed(false);
    }
  }, [isKeyboardShortcutsPaneOpen]);

  useEffect(() => {
    if (!isPresent(navLinks)) return;
    const currentLink = findLast(({ to, isActive }) => {
      const url = new URL(to, window.location.href);

      return is(Function, isActive)
        ? isActive()
        : location.pathname.includes(url.pathname);
    })(navLinks);

    if (isPresent(currentLink)) {
      setSelectedNavLinkRoute(
        currentLink.isConfigureNavLink ? currentLink.to : null
      );
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isLargerScreen()) return;
      setIsSidebarPaneOpen(false);
      setIsSidebarCollapsed(true);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isExpandable) return undefined;

    const handle = document.querySelector(
      ".neeto-molecules-sidebar__resize-handle"
    );

    if (!handle) return undefined;

    const onMouseDown = e => {
      isMouseDownRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current =
        sidebarRef.current?.getBoundingClientRect().width ||
        DEFAULT_SIDEBAR_WIDTH;

      enableDraggingStyles();
    };

    const onMouseMove = e => {
      if (!isMouseDownRef.current) return;

      const dx = e.clientX - startXRef.current;
      let newWidth = startWidthRef.current + dx;

      newWidth = Math.max(
        MIN_SIDEBAR_WIDTH,
        Math.min(newWidth, MAX_SIDEBAR_WIDTH)
      );

      setSidebarWidth(`${newWidth}px`);
    };

    const onMouseUp = () => {
      if (!isMouseDownRef.current) return;

      isMouseDownRef.current = false;
      disableDraggingStyles();
    };

    handle.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      handle.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isExpandable]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--neeto-molecules-sidebar-width",
      `${parseInt(sidebarWidth) / 16}rem`
    );
  }, [sidebarWidth]);

  const handleGoBack = event => {
    event.preventDefault();
    setSelectedNavLinkRoute(null);
    const configureNavLink = getActiveConfigurePageLink({ navLinks, location });
    if (configureNavLink?.to) {
      history.push(configureNavLink.to);
    }
  };

  const activeLink = useMemo(
    () =>
      navLinks.find(link => {
        if (is(Function, link.isActive)) return link.isActive();
        const url = new URL(link.to, window.location.href);

        return location.pathname.startsWith(url.pathname);
      }),
    [location.pathname, navLinks]
  );

  const isSelectedNavLinkVisible =
    selectedNavLinkRoute &&
    activeLink?.to === selectedNavLinkRoute &&
    isNotEmpty(navLinks);

  return (
    <>
      <div
        data-testid="sidebar-container"
        ref={sidebarRef}
        style={{ width: isSidebarCollapsed ? "0" : sidebarWidth }}
        className={classnames("neeto-molecules-sidebar__wrapper", {
          "neeto-molecules-sidebar__wrapper--collapsed": isLargerScreen()
            ? isSidebarCollapsed
            : not(isSidebarPaneOpen),
        })}
      >
        <div
          className="neeto-molecules-sidebar__backdrop"
          onClick={() => {
            setIsSidebarPaneOpen(false);
            setSelectedNavLinkRoute(null);
          }}
        />
        <div
          data-testid="sidebar-wrapper"
          className={classnames(
            "neeto-molecules-sidebar neeto-ui-flex neeto-ui-flex-col neeto-ui-flex-shrink-0 neeto-ui-overflow-y-auto neeto-ui-overflow-x-hidden",
            {
              "neeto-molecules-sidebar--focus-mode":
                isPresent(selectedNavLinkRoute),
            }
          )}
        >
          <Header
            {...{ customLogo }}
            clearSettingsNav={() => setSelectedNavLinkRoute(null)}
          />
          {showWorkspaceName && (
            <div className="neeto-ui-bg-gray-100 neeto-molecules-sidebar__workspace-info">
              <Typography
                className="neeto-ui-mb-1"
                style="h5"
                weight="semibold"
              >
                {t("neetoMolecules.sidebar.myWorkspace")}
              </Typography>
              <Typography className="neeto-ui-truncate" style="body2">
                {globalProps.organization.name}
              </Typography>
            </div>
          )}
          {isSelectedNavLinkVisible ? (
            <SelectedLink {...{ handleGoBack }} navLink={activeLink} />
          ) : (
            <Links
              {...{
                isCountsLoading,
                moreNavLinks,
                navLinks,
                setSelectedNavLinkRoute,
              }}
            />
          )}
        </div>
        <Button
          className="neeto-molecules-sidebar__toggler"
          data-testid="neeto-molecules-sidebar-toggler"
          icon={() => <Chevron />}
          style={isLargerScreen() ? "text" : "secondary"}
          tooltipProps={{
            content: isSidebarClosed
              ? t("neetoMolecules.sidebar.showMenu")
              : t("neetoMolecules.sidebar.hideMenu"),
            position: "bottom",
            touch: ["hold", 500],
          }}
          onClick={() => {
            setIsSidebarCollapsed(!isSidebarCollapsed);
            setIsSidebarPaneOpen(not);
          }}
        />
      </div>
      {isExpandable && (
        <div className="neeto-molecules-sidebar__resize-handle" />
      )}
    </>
  );
};

Sidebar.propTypes = {
  /**
   * To specify the navlinks to be shown in sidebar.
   */
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
      label: PropTypes.string,
      to: PropTypes.string,
      onClick: PropTypes.func,
      count: PropTypes.number,
      isActive: PropTypes.func,
      checkpointKey: PropTypes.string,
      isCountsLoading: PropTypes.bool,
      tag: PropTypes.object,
      addActions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          label: PropTypes.string,
          onClick: PropTypes.func,
        })
      ),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          to: PropTypes.string,
          onClick: PropTypes.func,
          href: PropTypes.string,
          count: PropTypes.number,
          isActive: PropTypes.func,
          type: PropTypes.string,
          filters: PropTypes.array,
          checkpointKey: PropTypes.string,
          tag: PropTypes.object,
        })
      ),
    })
  ),
  /**
   * To specify the navlinks to be shown in the dropdown which opens up on clicking More option in sidebar.
   */
  moreNavLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
      label: PropTypes.string,
      to: PropTypes.string,
      onClick: PropTypes.func,
      count: PropTypes.number,
      isActive: PropTypes.func,
      checkpointKey: PropTypes.string,
      isCountsLoading: PropTypes.bool,
      tag: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          to: PropTypes.string,
          onClick: PropTypes.func,
          href: PropTypes.string,
          count: PropTypes.number,
          isActive: PropTypes.func,
          type: PropTypes.string,
          filters: PropTypes.array,
          checkpointKey: PropTypes.string,
          tag: PropTypes.object,
        })
      ),
    })
  ),
  /**
   * To override the logo displayed on the sidebar.
   */
  customLogo: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  /**
   * Boolean to notate whether the counts are being fetched.
   */
  isCountsLoading: PropTypes.bool,
  /**
   * Boolean to determine whether the workspace name should be shown.
   */
  showWorkspaceName: PropTypes.bool,
  /**
   * Boolean to determine whether the sidebar should be expandable.
   */
  isExpandable: PropTypes.bool,
};

export default Sidebar;
